import { useEffect } from 'react'
import Navigation from '@/components/Navigation'
import brothersImage from '@assets/DSC_8002_copy_1768262295009.jpg'
import loveImage from '@assets/DSC_8328_copy_1768263625988.jpg'

const filmmakers = [
  { name: 'André Van Driessche', role: 'executive producer' },
  { name: 'Anna Kaperska', role: 'editor/post production supervisor', url: 'https://www.imdb.com/name/nm13559742/?ref_=fn_t_1' },
  { name: 'Bogdan Darev', role: 'writer/executive producer/director', url: 'https://www.imdb.com/name/nm1379696/' },
  { name: 'Brian Sloss', role: 'sound designer and mixer', url: 'https://www.imdb.com/name/nm9069950/?ref_=fn_t_1' },
  { name: 'Clayton Vermulm', role: 'additional writing' },
  { name: 'Jared Van Driessche', role: 'executive producer', url: 'https://www.imdb.com/name/nm14180414/?ref_=fn_t_1' },
  { name: 'Nari Fateha', role: 'co-producer' },
  { name: 'Mariya Apostolova', role: 'production designer', url: 'https://www.imdb.com/name/nm3328182/?ref_=fn_t_1' },
  { name: 'Matthew Skala', role: 'cinematographer', url: 'https://www.imdb.com/name/nm1417011/?ref_=fn_t_1' },
  { name: 'Micah Knapp', role: 'executive producer', url: 'https://www.imdb.com/name/nm2608271/?ref_=nv_sr_srsg_0_tt_1_nm_1_in_0_q_micah%2520knapp' },
  { name: 'Yulia Nikolova', role: 'executive producer', url: 'https://www.imdb.com/name/nm6776897/?ref_=fn_t_1' },
]

const DirectorVisionPage = () => {
  useEffect(() => {
    document.title = "Director's Vision | The Monsters We Make"
  }, [])

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Navigation />

      <main>
        <section className="pt-32 pb-8 md:pt-28 md:pb-8 lg:pt-32 lg:pb-16 px-6">
          <div className="max-w-6xl mx-auto grid md:grid-cols-[minmax(0,1fr)_minmax(0,0.78fr)] lg:grid-cols-[minmax(0,1fr)_420px] gap-8 md:gap-8 lg:gap-16 items-center">
            <div className="min-w-0">
              <p className="font-poppins text-red-600 text-sm uppercase tracking-[0.3em] mb-5">
                The Monsters We Make
              </p>
              <h1 className="font-bebas text-6xl md:text-7xl lg:text-8xl text-white tracking-wide leading-none mb-8">
                Director&apos;s <span className="text-red-600">Vision</span>
              </h1>
              <div className="font-poppins text-lg text-neutral-300 leading-relaxed space-y-6">
                <p>
                  Jared and André&apos;s story is rooted in lived reality. Nearly everything in the film is based on real people and events. Their pain and past bleed into their days. Time and trauma are not linear. Time and trauma are unforgiving. Over the last year, I traveled to Alaska and LA, spent time with the two brothers, and listened to their harrowing, tragic and often humorous stories firsthand.
                </p>
                <p>
                  Coming from a documentary background, I treat narrative cinema with the same respect for truth. I want to be there — in kitchens, locker rooms, scuffles, memories and nightmares. I want to get in Jared&apos;s head, to hold up a mirror for audiences. I must observe André roam the dark night of his soul. And through observation, together, we can change reality.
                </p>
              </div>
            </div>

            <div className="relative min-w-0 overflow-hidden">
              <img
                src={brothersImage}
                alt="Jared and André together"
                className="block w-full h-auto grayscale"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent"></div>
              <p className="absolute inset-x-0 bottom-0 p-5 md:p-6 lg:p-8 font-bebas text-3xl md:text-4xl lg:text-5xl tracking-wide leading-none">
                Time and trauma are <span className="text-red-600">not linear</span>
              </p>
            </div>
          </div>
        </section>

        <section className="pt-8 pb-20 md:pt-8 md:pb-14 lg:py-20 px-6">
          <div className="max-w-5xl mx-auto grid md:grid-cols-[minmax(0,360px)_minmax(0,1fr)] gap-8 md:gap-8 lg:gap-16 items-center">
            <img
              src={loveImage}
              alt="Fist with a LOVE tattoo"
              className="w-full h-auto grayscale order-2 md:order-1 mt-2 md:mt-0"
            />
            <div className="min-w-0 font-poppins text-lg text-neutral-300 leading-relaxed order-1 md:order-2">
              <p className="mb-6">
                Why this story? Because I admire Jared and André&apos;s vulnerability and courage to shine a light on the monsters that live in us all. Because I&apos;ve broken doors, walls and broom sticks in fits of rage. Because I drove like a maniac, but luckily, never off a bridge. Because some stories just must be told.
              </p>
              <p className="text-neutral-500 text-base">
                — Bogdan Darev, <span className="lowercase">writer/director/executive producer</span>
              </p>
            </div>
          </div>
        </section>

        <section className="py-20 px-8 md:px-12 lg:px-20 bg-black/40 border-y border-neutral-900">
          <div className="max-w-4xl mx-auto">
            <p className="font-poppins text-red-600 text-sm uppercase tracking-[0.3em] text-center mb-5">
              The Filmmakers
            </p>
            <h2 className="font-bebas text-4xl md:text-7xl text-white tracking-wide text-center leading-none mb-14 max-w-3xl mx-auto">
              A Film Is as Good as<br />the People Making It
            </h2>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
              {filmmakers.map((filmmaker) => {
                const details = (
                  <>
                    <p className="font-poppins text-white group-hover:text-red-600 transition-colors">
                      {filmmaker.name}
                    </p>
                    <p className="font-poppins text-neutral-500 text-sm lowercase mt-1">
                      {filmmaker.role}
                    </p>
                  </>
                )

                return filmmaker.url ? (
                  <a
                    key={filmmaker.name}
                    href={filmmaker.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group border-t border-neutral-800 pt-5"
                  >
                    {details}
                  </a>
                ) : (
                  <div key={filmmaker.name} className="group border-t border-neutral-800 pt-5">
                    {details}
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        <section className="py-16 px-6 bg-black">
          <div className="max-w-2xl mx-auto text-center">
            <p className="font-poppins text-neutral-400 text-base mb-6">
              Want to follow this film as it develops?
            </p>
            <a
              data-formkit-toggle="712c011a78"
              href="https://filmabee-studios.kit.com/712c011a78"
              className="inline-block font-bebas text-xl tracking-widest px-10 py-3 border border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors cursor-pointer"
            >
              STAY UPDATED
            </a>
          </div>
        </section>

        <section className="py-20 px-6 text-center">
          <a
            href="/the-monsters-we-make"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-red-600 transition-colors font-poppins text-sm tracking-wide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
            Back to the Film
          </a>
        </section>
      </main>
    </div>
  )
}

export default DirectorVisionPage